import { IInputProps } from '@/components';
import { IAutoComplete } from '@/components/form/auto-complete';
import {
	MasterInfoEnum,
	PageTypeEnum,
	SearchFilterTypes,
	TableTypes,
} from '@/constant';
import { TGetDataSearchFilter } from '@/services';

import { DefaultOptionType } from 'antd/es/select';
import { SorterResult } from 'antd/es/table/interface';

import { Dayjs } from 'dayjs';
import { FormikValues } from 'formik';

import { ICheckbox } from '../checkbox';
import { ICheckboxGroup } from '../checkbox-group';
import { ISelectMasterInfo } from '../select-master-info';
import { TFilterDate } from './filter-date';
import { TFilterTime } from './filter-time';
import { IInputCheckbox } from './input-checkbox';

export type TOptions = {
	defaultOptions: TRootOptions[];
	rootOptions: TRootOptions[];
	options: TRootOptions[];
};

export type TState = {
	formikValues: FormikValues;
	isSearch: boolean;
	sortSql: string;
} & TOptions;

export enum EEditableFilterType {
	TEXT_INPUT = 'TEXT_INPUT',
	CURRENCY = 'CURRENCY',
	PHONE_NUMBER = 'PHONE_NUMBER',
	DROPDOWN = 'DROPDOWN',
	AUTOCOMPLETE = 'AUTOCOMPLETE',
	DATE = 'DATE',
	DATE_RANGE_PICKER = 'DATE_RANGE_PICKER',
	TIME_PICKER = 'TIME_PICKER',
	CHECKBOX = 'CHECKBOX',
	CHECKBOX_GROUP = 'CHECKBOX_GROUP',
	INPUT_TARGET = 'INPUT_TARGET',
	INPUT_CHECKBOX = 'INPUT_CHECKBOX',
	HIDDEN = 'HIDDEN',
}

export type TSelectDateValue = {
	type: EEditableFilterType;
	selectTime?: string;
	selectYear?: string;
	startDate?: Dayjs | null;
	endDate?: Dayjs | null;
	searchYes?: boolean | null;
	searchNo?: boolean | null;
};

export type TDateProps = Omit<TFilterDate, 'formik' | 'label' | 'name'>;

export type TSelectTimeValue = {
	type: EEditableFilterType;
	startTime: string | null;
	endTime: string | null;
};

export type TTimeProps = Omit<TFilterTime, 'formik' | 'label' | 'name'>;

export type TInputCheckboxValue = {
	type: EEditableFilterType;
	searchValue?: string | null;
	searchYes?: boolean | null;
	searchNo?: boolean | null;
};

export type TFilterItem = {
	type: EEditableFilterType;
	inputProps?: Partial<IInputProps>; // TEXT_INPUT
	inputCheckboxProps?: Partial<IInputCheckbox>; // INPUT_CHECKBOX
	dropdownProps?: Partial<ISelectMasterInfo>; // DROPDOWN
	dateProps?: Partial<TDateProps>; // DATE
	timeProps?: Partial<TTimeProps>; // TIME
	autoCompleteProps?: Partial<IAutoComplete>; // AUTOCOMPLETE
	checkboxProps?: Partial<ICheckbox>; // CHECKBOX
	checkboxGroupProps?: Partial<ICheckboxGroup>; // CHECKBOX_GROUP
	searchTypeConfigs?: ISearchTypeConfig[];
	getAdditionalValues?: (
		value: any,
		prevValues: any,
		option?: DefaultOptionType
	) => Partial<any> | void;
};

export type TFilterItemOrFn<T = TFilterItem> =
	| T
	| ((values: FormikValues) => T);

export type TRootOptions<T = TFilterItem> = {
	value: string;
	label: string;
	/**
	 * @description prefixKey is tableName, ex: APPLICANT, FRANCHISE,..
	 */
	prefixKey?: PageTypeEnum;
	filterItem?: TFilterItemOrFn<T>;
	/** @description If true, it is displayed by default on the screen */
	static?: boolean;
	order?: number;
};

export function resolveFilterItem<T = TFilterItem>(
	filterItem: TFilterItemOrFn<T> | undefined,
	values?: FormikValues
): T | undefined {
	if (typeof filterItem === 'function') {
		return (filterItem as (values: FormikValues) => T)(values || {});
	}
	return filterItem;
}

export type TSearchRequest = TGetDataSearchFilter & {
	[key in `${Lowercase<string>}SearchTypes`]?: TSearchRequest['searchTypes'];
};
export interface ISearchFilter {
	labelClassName?: string;
	containerClassName?: string;
	rootOptions: TRootOptions[];
	searchFilterType?: SearchFilterTypes;
	tableType?: TableTypes;
	initialParams?: {
		[key: string]: any;
		searchTypes?: TSearchRequest['searchTypes'];
	};
	ignoreFirstSearch?: boolean;
	hideAdditionalFilter?: boolean;
	onClickResetCallback?: () => void;
}

export interface ISearchFilterRef {
	onSearchChange?: (page?: number) => void;
	onPageChange?: (page: number, pageSize: number) => void;
	onPageSizeChange?: (
		value: DefaultOptionType['value'],
		option: DefaultOptionType | DefaultOptionType[]
	) => void;
	resetSearch?: () => void;
	isSearch?: boolean;
	isSearching?: boolean;
	sorter?: SorterResult<any> | SorterResult<any>[];
	setSorter?: (data: { sortSql: string }) => void;
	handleSetValue?: (
		values: FormikValues,
		options?: { includePrevValue?: boolean }
	) => void;
	handleSetFilter?: (keys: string[]) => void;
	onSearchTrigger?: () => void;
}

export type TInputTargetValue = {
	type: EEditableFilterType;
	searchType: string | null;
	searchValue: string | null;
};

export interface ISearchTypeConfig {
	value: string;
	inputType: 'input' | 'autocomplete';
	type?: MasterInfoEnum;
}
